/**
 * `<policy-predicate>` — the row-filter predicate tree (`SC-F512`, `SC-S511`,
 * `SC-S512`).
 *
 * A recursive element: a node renders itself and its children render this same tag.
 * Six rules are enforced here, each traceable to a defect the tree exists to
 * prevent.
 *
 *  1. **A node is exactly one of ALL / ANY / NOT / leaf.** Upstream,
 *     `compilePredicate` branches on the internal-node fields first and silently
 *     discards the leaf fields, and `renderPredicate` returns after the first
 *     non-empty in the order all → any → not. So a node carrying both loses half of
 *     itself with no error anywhere. The editor writes one shape per node, and the
 *     shape select converts rather than merging.
 *  2. **`column` is a single identifier with no dot.** It is emitted as one quoted
 *     identifier, so `o.tenant_id` becomes a column that does not exist and breaks
 *     the query at execution time — and Sluice does not validate it.
 *  3. **Operator semantics are shown inline**, from the schema's own per-value help:
 *     `Like` keeps `%` and `_` active, `StartsWith` / `EndsWith` / `Contains` are
 *     literal functions, `Matches` is a **partial** regex. Where the surprise has a
 *     one-click fix — anchoring a `Matches` pattern, switching a `Like` to
 *     `Contains` — the fix is offered.
 *  4. **The value type is explicit**, because comparison is type-strict.
 *  5. **`Between` takes exactly two values, low first**; `In` and `NotIn` at least
 *     one. The counts are enforced by the shared typed-value editor.
 *  6. **The emitted-SQL preview is always visible** at the root. It is an advisory
 *     mirror of the rewriter (`03` §3.14) and says so.
 *
 * Deep trees stay usable: every internal node collapses, indentation is bounded, and
 * the root scrolls horizontally inside its own container so the page body never
 * does.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../../components/index.js";
import type { BnSelectChangeDetail, BnSelectOption } from "../../components/index.js";
import type { PolicyFinding } from "../../api/policies.js";
import type { BindingSummary } from "./bindings.js";
import {
  emptyPredicate,
  shapeOf,
  type Predicate,
  type PredicateShape,
  type ScalarValue,
} from "./model.js";
import { diagnosticSeverity, findingsAt, findingsUnder, worst } from "./policy-findings.js";
import "./policy-typed-value.js";
import type { TypedValueChangeDetail } from "./policy-typed-value.js";
import { paramsFor } from "./sql-preview.js";
import type { PolicySchema } from "./schema.js";
import type { SchemaCache } from "./schema-cache.js";

/** Detail of `predicate-change`. */
export interface PredicateChangeDetail {
  node: Predicate;
}

/** Detail of `predicate-remove`, raised by a node that wants to be deleted. Only
 * the parent can remove it, because only the parent owns the array it sits in. */
export interface PredicateRemoveDetail {
  reason: "user";
}

/** The four shapes, as a select. The labels are the tree's own vocabulary and not a
 * DSL enum — `all`, `any` and `not` are field names, so there is no schema enum to
 * read them from; the schema's `predicate` group help is what states the
 * one-shape-per-node rule, and it is rendered verbatim at the root. */
const SHAPES: Array<{ value: PredicateShape; label: string; description: string }> = [
  { value: "leaf", label: "A single condition", description: "One column, one operator, one value." },
  { value: "all", label: "ALL of", description: "Every child must hold — AND." },
  { value: "any", label: "ANY of", description: "At least one child must hold — OR." },
  { value: "not", label: "NOT", description: "Negates its single child." },
];

/** How deep the tree indents before it stops adding inset, so a deliberately deep
 * tree cannot push the leaf editors off the side. */
const MAX_INDENT_DEPTH = 6;

@customElement("policy-predicate")
export class PolicyPredicate extends LitElement {
  @property({ attribute: false }) schema!: PolicySchema;
  /** This node. */
  @property({ attribute: false }) node: Predicate = emptyPredicate();
  /** The object path of this node, for finding anchors. */
  @property() path = "spec.filter.predicate";
  /** Nesting depth, for indentation and for the collapse affordance. */
  @property({ type: Number }) depth = 0;
  /** True for the tree's root, which owns the shape select and cannot be removed. */
  @property({ type: Boolean }) root = false;
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  /** The schema cache, for the column picker. */
  @property({ attribute: false }) cache: SchemaCache = {
    tables: [],
    degraded: true,
    warning: "",
    fetchedAt: "",
  };
  /** Template-picker inputs, forwarded down to every leaf value. */
  @property({ attribute: false }) bindings: BindingSummary[] = [];
  @property() binding = "";
  @property({ type: Number }) bindingTotal = 0;
  @property() claimsHelp = "";

  @state() private collapsed = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .node {
      border-left: 2px solid var(--bino-border);
      padding-left: var(--bino-space-md);
    }
    .node.root {
      border-left: none;
      padding-left: 0;
    }
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
    }
    .kind {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      color: var(--bino-primary);
    }
    .spacer {
      flex: 1;
    }
    .children {
      display: grid;
      gap: var(--bino-space-md);
    }
    .leaf {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: flex-start;
    }
    .leaf .col {
      flex: 1 1 14rem;
      min-width: 0;
    }
    .colwrap {
      display: grid;
      gap: var(--bino-space-xs);
    }
    .leaf .op {
      flex: 1 1 12rem;
      min-width: 0;
    }
    .value {
      margin-top: var(--bino-space-sm);
    }
    bn-field {
      margin-bottom: 0;
    }
    .note {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .fix {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: center;
      margin-top: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
    }
    .adders {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-sm);
    }
    .empty {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-base);
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      max-width: 80ch;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
  `;

  private get shape(): PredicateShape {
    return shapeOf(this.node);
  }

  private emit(node: Predicate) {
    this.dispatchEvent(
      new CustomEvent<PredicateChangeDetail>("predicate-change", {
        detail: { node },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private requestRemove() {
    this.dispatchEvent(
      new CustomEvent<PredicateRemoveDetail>("predicate-remove", {
        detail: { reason: "user" },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** The children of an internal node, whatever its shape. Named `kids` rather than
   * `children` because this class is an HTMLElement and that name is taken. */
  private get kids(): Predicate[] {
    if (this.node.all) return this.node.all;
    if (this.node.any) return this.node.any;
    if (this.node.not) return [this.node.not];
    return [];
  }

  /**
   * convert changes this node's shape.
   *
   * It writes a whole new node rather than adding a key, which is the mechanical
   * guarantee behind rule 1: there is no code path here that leaves two shapes
   * populated. Content is carried where a carry has a meaning — a leaf becomes the
   * single child of a new group, a group's children move to the other group kind, a
   * `NOT` gives up its child — and is dropped only where it cannot be represented,
   * which is a group of several children collapsing to one condition. That case is
   * refused rather than silently discarding children.
   */
  private convert(to: PredicateShape) {
    if (to === this.shape) return;
    const children = this.kids;
    if (to === "leaf") {
      const survivor = children.find((c) => shapeOf(c) === "leaf");
      this.emit(survivor ?? emptyPredicate());
      return;
    }
    const carried =
      this.shape === "leaf" ? (hasLeafContent(this.node) ? [this.node] : []) : children;
    if (to === "not") {
      this.emit({ not: carried[0] ?? emptyPredicate() });
      return;
    }
    this.emit(to === "all" ? { all: carried } : { any: carried });
  }

  /** True when converting to a single condition would discard something. */
  private get lossyToLeaf(): boolean {
    const children = this.kids;
    if (this.shape === "leaf") return false;
    return children.length > 1 || children.some((c) => shapeOf(c) !== "leaf");
  }

  private addChild(shape: PredicateShape) {
    const fresh: Predicate =
      shape === "leaf"
        ? emptyPredicate()
        : shape === "not"
          ? { not: emptyPredicate() }
          : shape === "all"
            ? { all: [emptyPredicate()] }
            : { any: [emptyPredicate()] };
    if (this.node.all) this.emit({ all: [...this.node.all, fresh] });
    else if (this.node.any) this.emit({ any: [...this.node.any, fresh] });
  }

  private setChild(index: number, child: Predicate) {
    if (this.node.all) {
      const next = [...this.node.all];
      next[index] = child;
      this.emit({ all: next });
    } else if (this.node.any) {
      const next = [...this.node.any];
      next[index] = child;
      this.emit({ any: next });
    } else if (this.node.not) {
      this.emit({ not: child });
    }
  }

  private dropChild(index: number) {
    if (this.node.all) this.emit({ all: this.node.all.filter((_, i) => i !== index) });
    else if (this.node.any) this.emit({ any: this.node.any.filter((_, i) => i !== index) });
    else if (this.node.not) this.emit({ not: emptyPredicate() });
  }

  // ── Leaf ────────────────────────────────────────────────────────────────

  /**
   * The cached column names, as completions.
   *
   * They are offered **beside** a free-text field and never instead of it. The cache
   * is a snapshot of what was introspected, so a column it does not list may still
   * exist — a table the refresh could not reach, a view added since — and a picker
   * that was the only way in would make the editor claim the schema cache is
   * authoritative about the customer's database. It is not.
   */
  private get columnOptions(): BnSelectOption[] {
    const names = new Set<string>();
    for (const t of this.cache.tables) for (const c of t.columns) names.add(c);
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }

  private renderLeaf() {
    const group = "predicate";
    const opField = this.schema.field(group, "op");
    const valueField = this.schema.field(group, "value");
    const valuesField = this.schema.field(group, "values");
    const op = this.node.op ?? "";
    const siblings = { op };
    const columnFinding = worst(findingsAt(this.findings, `${this.path}.column`));
    const opFinding = worst(findingsAt(this.findings, `${this.path}.op`));
    const valueFinding = worst(findingsAt(this.findings, `${this.path}.value`));
    const dotted = (this.node.column ?? "").includes(".");
    const takesValue = this.schema.required(valueField, siblings);
    const takesValues = this.schema.required(valuesField, siblings);

    return html`
      <div class="leaf">
        <div class="col">
          <bn-field
            label="Column"
            required
            hint=${this.schema.help(group, "column")}
            error=${dotted
              ? "A predicate column is emitted as one quoted identifier, so a dotted value becomes a column that does not exist and the query breaks at execution time. Sluice does not validate this."
              : (columnFinding?.message ?? "")}
            rule=${dotted ? "L02" : (columnFinding?.rule ?? "")}
            severity=${dotted
              ? "error"
              : columnFinding
                ? diagnosticSeverity(columnFinding.severity)
                : "error"}
          >
            <div class="colwrap">
              <bn-input
                mono
                placeholder="tenant_id"
                .value=${this.node.column ?? ""}
                @input=${(e: CustomEvent<{ value: string }>) => {
                  e.stopPropagation();
                  this.emit({ ...this.node, column: e.detail.value });
                }}
              ></bn-input>
              ${this.cache.degraded
                ? nothing
                : html`<bn-select
                    filterable
                    aria-label="Pick a column from the cached schema"
                    .options=${this.columnOptions}
                    .value=${""}
                    placeholder="…or pick from the cached schema"
                    emptyText="No cached column matches."
                    @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                      e.stopPropagation();
                      this.emit({ ...this.node, column: e.detail.value });
                    }}
                  ></bn-select>`}
            </div>
          </bn-field>
        </div>
        <div class="op">
          <bn-field
            label="Operator"
            required
            hint=${this.schema.enumHelp(opField?.enum)}
            error=${opFinding?.message ?? ""}
            rule=${opFinding?.rule ?? ""}
            severity=${opFinding ? diagnosticSeverity(opFinding.severity) : "error"}
          >
            <bn-select
              filterable
              .options=${this.schema.options(opField?.enum)}
              .value=${op}
              placeholder="Select an operator…"
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                e.stopPropagation();
                this.changeOp(e.detail.value);
              }}
            ></bn-select>
          </bn-field>
        </div>
      </div>
      ${this.renderOperatorNote(op)} ${this.renderLiteralOffer(op)}
      ${takesValue
        ? html`<div class="value">
            <policy-typed-value
              label="Value"
              allow-template
              .help=${this.schema.help(group, "value")}
              .typeOptions=${this.schema.options("ValueType")}
              .typesHelp=${this.schema.enumHelp("ValueType")}
              .value=${this.node.value}
              .bindings=${this.bindings}
              .binding=${this.binding}
              .bindingTotal=${this.bindingTotal}
              .claimsHelp=${this.claimsHelp}
              .diagnostic=${valueFinding?.message ?? ""}
              .rule=${valueFinding?.rule ?? ""}
              .severity=${valueFinding ? diagnosticSeverity(valueFinding.severity) : "error"}
              @value-change=${(e: CustomEvent<TypedValueChangeDetail>) => {
                e.stopPropagation();
                this.emit({ ...this.node, value: e.detail.value });
              }}
            ></policy-typed-value>
            ${this.renderAnchorOffer(op)}
          </div>`
        : nothing}
      ${takesValues
        ? html`<div class="value">
            <policy-typed-value
              label="Values"
              multiple
              allow-template
              .exactly=${op === "Between" ? 2 : 0}
              .minItems=${valuesField?.minItems ?? 1}
              .help=${this.schema.help(group, "values")}
              .typeOptions=${this.schema.options("ValueType")}
              .typesHelp=${this.schema.enumHelp("ValueType")}
              .values=${this.node.values ?? []}
              .bindings=${this.bindings}
              .binding=${this.binding}
              .bindingTotal=${this.bindingTotal}
              .claimsHelp=${this.claimsHelp}
              @value-change=${(e: CustomEvent<TypedValueChangeDetail>) => {
                e.stopPropagation();
                this.emit({ ...this.node, values: e.detail.values });
              }}
            ></policy-typed-value>
          </div>`
        : nothing}
      ${op !== "" && paramsFor(op, (this.node.values ?? []).length) === 0
        ? html`<p class="note">This operator takes no value.</p>`
        : nothing}
    `;
  }

  /**
   * changeOp drops the value fields the new operator does not take.
   *
   * Leaving them would be worse than untidy: `values` beside an `IsNull` renders into
   * the YAML and reads as a constraint the engine never applies, which is the exact
   * class of confusion the mask-argument strip exists to prevent elsewhere.
   */
  private changeOp(op: string) {
    const next: Predicate = { column: this.node.column, op };
    const valueField = this.schema.field("predicate", "value");
    const valuesField = this.schema.field("predicate", "values");
    if (this.schema.required(valueField, { op }) && this.node.value !== undefined) {
      next.value = this.node.value;
    }
    if (this.schema.required(valuesField, { op }) && this.node.values) {
      next.values = this.node.values;
    }
    this.emit(next);
  }

  /** The selected operator's own authoring note, straight from the schema's enum
   * help — the emitted SQL and the surprise in it. */
  private renderOperatorNote(op: string) {
    if (op === "") return nothing;
    const help = this.schema
      .enumOf(this.schema.field("predicate", "op")?.enum)
      ?.values?.find((v) => v.value === op)?.help;
    if (!help) return nothing;
    return html`<p class="note">${help}</p>`;
  }

  /**
   * `Like` and `NotLike` keep `%` and `_` active as metacharacters, so a value that
   * merely contains one silently means something else. The three literal operators
   * need no escaping at all, so they are offered as a switch rather than mentioned in
   * help text the author has already read past.
   */
  private renderLiteralOffer(op: string) {
    if (op !== "Like" && op !== "NotLike") return nothing;
    const value = typeof this.node.value === "string" ? this.node.value : "";
    if (!value.includes("%") && !value.includes("_")) return nothing;
    const swap = (to: string) => () => this.changeOp(to);
    return html`<div class="fix">
      <span
        >This value contains <code>%</code> or <code>_</code>, which stay active as wildcards. For a
        literal comparison use one of these instead — they need no escaping.</span
      >
      <bn-button size="sm" @click=${swap("Contains")}>Contains</bn-button>
      <bn-button size="sm" @click=${swap("StartsWith")}>Starts with</bn-button>
      <bn-button size="sm" @click=${swap("EndsWith")}>Ends with</bn-button>
    </div>`;
  }

  /** `Matches` is a partial regex, so an author meaning "the whole value" must
   * anchor it. The offer is here rather than in the help text because the fix is one
   * click and the mistake is silent. */
  private renderAnchorOffer(op: string) {
    if (op !== "Matches") return nothing;
    const value = this.node.value;
    if (typeof value !== "string" || value === "") return nothing;
    if (value.startsWith("^") && value.endsWith("$")) return nothing;
    return html`<div class="fix">
      <span
        >This is a <strong>partial</strong> match, so it also matches values that merely contain the
        pattern.</span
      >
      <bn-button
        size="sm"
        @click=${() =>
          this.emit({
            ...this.node,
            value: `${value.startsWith("^") ? "" : "^"}${value}${value.endsWith("$") ? "" : "$"}`,
          })}
      >
        Anchor with ^…$
      </bn-button>
    </div>`;
  }

  // ── Internal nodes ──────────────────────────────────────────────────────

  private renderChildren() {
    const children = this.kids;
    if (children.length === 0) {
      return html`<p class="empty">
        This group has no children, so it contributes nothing to the filter and the whole predicate
        may render empty — which Sluice refuses at compile.
      </p>`;
    }
    const childPath = (i: number) =>
      this.node.not ? `${this.path}.not` : `${this.path}.${this.node.all ? "all" : "any"}[${i}]`;
    return html`<div class="children">
      ${children.map(
        (child, i) => html`<policy-predicate
          .schema=${this.schema}
          .node=${child}
          .path=${childPath(i)}
          .depth=${this.depth + 1}
          .findings=${this.findings}
          .cache=${this.cache}
          .bindings=${this.bindings}
          .binding=${this.binding}
          .bindingTotal=${this.bindingTotal}
          .claimsHelp=${this.claimsHelp}
          @predicate-change=${(e: CustomEvent<PredicateChangeDetail>) => {
            e.stopPropagation();
            this.setChild(i, e.detail.node);
          }}
          @predicate-remove=${(e: CustomEvent<PredicateRemoveDetail>) => {
            e.stopPropagation();
            this.dropChild(i);
          }}
        ></policy-predicate>`,
      )}
    </div>`;
  }

  private renderShapeSelect() {
    const options: BnSelectOption[] = SHAPES.map((s) => {
      const option: BnSelectOption = { value: s.value, label: s.label, description: s.description };
      if (s.value === "leaf" && this.lossyToLeaf) {
        option.disabled = true;
        option.disabledReason =
          "This group holds more than one condition. Remove or flatten them first — converting would discard children, and a predicate that silently loses half of itself is the defect this editor exists to prevent.";
      }
      return option;
    });
    return html`<bn-select
      aria-label="Node kind"
      .options=${options}
      .value=${this.shape}
      @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
        e.stopPropagation();
        this.convert(e.detail.value as PredicateShape);
      }}
    ></bn-select>`;
  }

  override render() {
    if (!this.schema) return nothing;
    const shape = this.shape;
    const internal = shape !== "leaf";
    const subtree = findingsUnder(this.findings, this.path);
    const badge = worst(subtree);
    const indent = Math.min(this.depth, MAX_INDENT_DEPTH);
    return html`
      <div
        class="node ${this.root ? "root" : ""}"
        style=${indent > 0 ? "" : "border-left-color: transparent"}
      >
        <div class="head">
          ${internal
            ? html`<bn-button
                size="sm"
                variant="ghost"
                @click=${() => (this.collapsed = !this.collapsed)}
                aria-expanded=${this.collapsed ? "false" : "true"}
              >
                ${this.collapsed ? "Expand" : "Collapse"}
              </bn-button>`
            : nothing}
          <span class="kind"
            >${shape === "leaf" ? "condition" : shape === "not" ? "NOT" : `${shape.toUpperCase()} of`}</span
          >
          ${badge
            ? html`<bn-badge variant=${badge.severity === "error" ? "error" : "warning"}
                >${badge.rule}</bn-badge
              >`
            : nothing}
          <span class="spacer"></span>
          ${this.renderShapeSelect()}
          ${this.root
            ? nothing
            : html`<bn-button
                size="sm"
                variant="ghost"
                @click=${() => this.requestRemove()}
                aria-label="Remove this node"
              >
                Remove
              </bn-button>`}
        </div>
        ${this.collapsed
          ? html`<p class="note">
              ${this.kids.length} ${this.kids.length === 1 ? "child" : "children"} hidden.
            </p>`
          : internal
            ? html`${this.renderChildren()}
                ${shape === "not"
                  ? nothing
                  : html`<div class="adders">
                      <bn-button size="sm" @click=${() => this.addChild("leaf")}>
                        Add condition
                      </bn-button>
                      <bn-button size="sm" @click=${() => this.addChild("all")}>
                        Add ALL group
                      </bn-button>
                      <bn-button size="sm" @click=${() => this.addChild("any")}>
                        Add ANY group
                      </bn-button>
                      <bn-button size="sm" @click=${() => this.addChild("not")}>
                        Add NOT group
                      </bn-button>
                    </div>`}`
            : this.renderLeaf()}
      </div>
    `;
  }
}

/** hasLeafContent reports whether a leaf carries anything worth carrying into a new
 * group — an empty one is dropped rather than nesting a blank condition. */
function hasLeafContent(node: Predicate): boolean {
  const value: ScalarValue | undefined = node.value;
  return (
    (node.column ?? "") !== "" ||
    (node.op ?? "") !== "" ||
    value !== undefined ||
    (node.values ?? []).length > 0
  );
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-predicate": PolicyPredicate;
  }
}
